import React, {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { ExclamationIcon, TrashIcon } from "@heroicons/react/outline";
import ExportDropdown from "./ExportDropdown";
import { Button, Menu, Dialog } from "ui";
import { EXPORT_DROPDOWN_NAME } from "./utils";

type BaseProps = {
  label: React.ReactNode;
  tableName: React.ReactNode;
  onConfirm: (info: { method: string; type: string }) => void;
  rowsCount: number;
  dataCount: number;
};

type ButtonWithConfirmModalProps = BaseProps & {
  type: string;
  noButton?: boolean;
  forceOpen?: boolean;
  onChangeOpenStatus?: (isOpen: boolean) => void;
  onDeselectAll: () => void;
  hasPermission: boolean;
};

type TableConfirmationProps = BaseProps & {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  actionForConfirmation: {
    method: string;
    type: string;
  };
};

export const TableConfirmation = ({
  open,
  label,
  tableName,
  setOpen,
  onConfirm,
  actionForConfirmation,
  rowsCount,
  dataCount,
}: TableConfirmationProps) => {
  const deleteButtonRef = useRef(null);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Dialog.Popup className="px-4 pb-4 pt-5 sm:p-6 ">
        <div className="sm:flex sm:items-start">
          <div className="mx-auto flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-danger-subtler sm:mx-0 sm:h-10 sm:w-10">
            <ExclamationIcon
              className="h-6 w-6 text-danger"
              aria-hidden="true"
            />
          </div>
          <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
            <Dialog.Title>
              {label} {tableName}s
            </Dialog.Title>

            <Dialog.Description className="mt-2">
              Are you sure you want to {label}{" "}
              <span className="font-bold">
                {actionForConfirmation.type === "export_all"
                  ? `all ${dataCount}`
                  : rowsCount}{" "}
                {tableName}s
              </span>
              {label === "Delete" && (
                <span>
                  ? All of your data will be permanently removed from our
                  servers forever. This action cannot be undone.
                </span>
              )}
            </Dialog.Description>
          </div>
        </div>

        <Dialog.Footer variant="bare" className="mt-5 sm:mt-6">
          <Button
            colorScheme="danger"
            className="flex-1"
            onClick={() => {
              onConfirm(actionForConfirmation);
              setOpen(false);
            }}
            ref={deleteButtonRef}
          >
            {label === "Cancel" ? "Confirm Cancel" : label}
          </Button>
          <Button
            className="flex-1"
            variant="secondary"
            onClick={() => setOpen(false)}
          >
            Cancel
          </Button>
        </Dialog.Footer>
      </Dialog.Popup>
    </Dialog>
  );
};

const ButtonWithConfirmModal: React.FC<ButtonWithConfirmModalProps> = ({
  label,
  type,
  tableName,
  rowsCount,
  dataCount,
  onConfirm,
  noButton,
  forceOpen,
  hasPermission,
  onChangeOpenStatus,
  onDeselectAll,
}) => {
  const [open, setOpen] = useState(false);
  const [actionForConfirmation, setActionForConfirmation] = useState({
    method: "",
    type,
  });

  useEffect(() => {
    if (noButton) {
      setOpen(forceOpen!);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceOpen]);

  useEffect(() => {
    if (noButton) {
      onChangeOpenStatus!(open);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const getExportDropdownOptions = useCallback((): [string, string][] => {
    return [
      ["export_selected", `Export ${rowsCount} ${tableName}s on this page`],
      ["export_all", `Export all ${dataCount} ${tableName}s`],
      ["deselect_all", "Deselect all"],
    ];
  }, [rowsCount, tableName, dataCount]);

  if (!hasPermission) {
    return null;
  }

  return (
    <React.Fragment>
      {!noButton ? (
        <Fragment>
          {type === EXPORT_DROPDOWN_NAME ? (
            <ExportDropdown
              options={getExportDropdownOptions()}
              onSelect={(selection) => {
                if (selection[0] === "deselect_all") {
                  onDeselectAll();
                } else {
                  setOpen(true);
                  setActionForConfirmation({
                    method: "post",
                    type: selection[0],
                  });
                }
              }}
            />
          ) : (
            <Menu.Item
              variant={label === "Delete" ? "destructive" : undefined}
              className="flex w-full items-center justify-between gap-3 px-4 py-2"
              onClick={(e) => {
                e.stopPropagation();
                setOpen(true);
              }}
              render={<button type="button" />}
              disabled={!hasPermission}
              closeOnClick={false}
            >
              {label} Selected
              {label === "Delete" && <Menu.Icon Icon={TrashIcon} />}
            </Menu.Item>
          )}
        </Fragment>
      ) : null}

      <TableConfirmation
        label={label}
        tableName={tableName}
        onConfirm={onConfirm}
        open={open}
        setOpen={setOpen}
        actionForConfirmation={actionForConfirmation}
        rowsCount={rowsCount}
        dataCount={dataCount}
      />
    </React.Fragment>
  );
};
export default ButtonWithConfirmModal;
